import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button as RBButton, Form, Row, Col } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faCalendarAlt, faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Select from "react-select";
import { useLazyQuery, useMutation } from "@apollo/client";
import { toast } from "react-toastify";

// Queries and Mutations
import { SELLER_PRODUCTS_QUERY } from "api/graphql/queries/product/GetInvProducts";
import { GET_PRODUCT_DETAILS } from "api/graphql/queries/product/GetProductsDetails";
import { UPDATE_INVENTORY_REQUEST } from "api/graphql/mutations/inventory/updateInventoryRequest";

// Types
export interface InventoryItem {
  ID: string;
  Qty: string;
  product_id?: string;
  quantity_done?: number;
  productName?: string;
}

export interface InventoryRequest {
  inv_id: string;
  date: string;
  status?: string;
  scheduledDate?: string;
  inventory_list?: InventoryItem[];
  "date Demandé"?: string;
  "date prévue"?: string;
  "Move Type"?: string;
  "Stock Type"?: boolean | string;
}

interface ProductOption {
  value: string;
  label: string;
  image: string;
  seller_reference: string;
  stockQty: number;
}

// Edit Inventory Line for UI management
export interface EditInventoryLine {
  ID: string;
  Qty: string;
  product_id: string;
  quantity: number;
  productName?: string;
  isNew?: boolean; // Track if this is a newly added product
  currentStock?: number; // Current stock quantity from product details API
  imageUrl?: string; // Product image URL
  seller_reference?: string; // Product seller reference/SKU
}

interface EditInventoryRequestModalProps {
  show: boolean;
  onHide: () => void;
  request: InventoryRequest | null;
  onRequestUpdated: () => void;
}

const EditInventoryRequestModal: React.FC<EditInventoryRequestModalProps> = ({
  show, 
  onHide, 
  request, 
  onRequestUpdated
}) => {
  const { t } = useTranslation();
  // State
  const [editDate, setEditDate] = useState<Date | undefined>(undefined);
  const [editItems, setEditItems] = useState<EditInventoryLine[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);
  const [originalDate, setOriginalDate] = useState<Date | undefined>(undefined);
  const [originalItems, setOriginalItems] = useState<EditInventoryLine[]>([]);
  
  // Queries & Mutations
  const [fetchProducts, { data: productsData, loading: productsLoading }] = useLazyQuery(SELLER_PRODUCTS_QUERY);
  const [fetchProductDetails, { loading: detailsLoading }] = useLazyQuery(GET_PRODUCT_DETAILS);
  const [updateInventoryRequestMutation, { loading: updateLoading }] = useMutation(UPDATE_INVENTORY_REQUEST);
  
  // Set initial data when the modal opens
  useEffect(() => {
    if (show && request) {
      // Set the date
      const initialDate = new Date(request.date);
      setEditDate(initialDate);
      setOriginalDate(initialDate);
      
      // Load inventory items
      const inventoryList = request.inventory_list || [];
      
      // Extract all product IDs to fetch at once
      const productIds = inventoryList
        .map(item => item.product_id || item.ID || "")
        .filter(id => id !== "");
      
      if (productIds.length > 0) {
        // Fetch all product details at once using the IDs filter
        fetchProducts({
          variables: {
            page: 1,
            pageSize: productIds.length,
            filter: { 
              ids: productIds
            },
          },
        }).then(({ data }) => {
          if (data?.sellerProducts?.products) {
            // Create a map of product details by ID for quick lookup
            const productDetailsMap = new Map();
            data.sellerProducts.products.forEach((product: any) => {
              productDetailsMap.set(product.entityid.toString(), {
                name: product.name || "Unknown Product",
                stock: product.stock_data?.qty || 0,
                imageUrl: product.image1?.url || "https://via.placeholder.com/50",
                seller_reference: product.seller_reference || "No SKU"
              });
            });
            
            // Map inventory items with product details
            const items = inventoryList.map((item) => {
              const productId = item.product_id || item.ID || "";
              const quantity_done = typeof item.quantity_done === 'number' ? item.quantity_done : 0;
              const qtyFromString = parseInt(item.Qty || "0") || 0;
              const itemQty = quantity_done || qtyFromString;
              
              const productDetails = productDetailsMap.get(productId) || {
                name: "Unknown Product",
                stock: 0,
                imageUrl: "https://via.placeholder.com/50",
                seller_reference: "No REF"
              };
              
              return {
                ID: item.ID || item.product_id || "",
                Qty: item.Qty || (item.quantity_done?.toString() || "0"),
                product_id: productId,
                quantity: itemQty,
                productName: productDetails.name,
                currentStock: productDetails.stock,
                imageUrl: productDetails.imageUrl,
                seller_reference: productDetails.seller_reference,
                isNew: false
              };
            });
            
            console.log("Loaded items:", items);
            setEditItems(items);
            setOriginalItems([...items]); // Store original items for change detection
            setHasFormChanges(false); // Reset changes flag when modal opens
          }
        }).catch(error => {
          console.error("Error fetching product details:", error);
          toast.error("Error loading product details");
        });
      } else {
        // No product IDs to fetch
        setEditItems([]);
        setOriginalItems([]);
        setHasFormChanges(false);
      }
    }
  }, [show, request, fetchProducts]);
  
  // Initial fetch of products when modal opens
  useEffect(() => {
    if (show && request) {
      // This will apply all the correct filters based on the request's move type and stock type
      loadProducts("");
    }
  }, [show, request]);
  
  // Process fetched products into options
  useEffect(() => {
    if (productsData?.sellerProducts?.products) {
      const options = productsData.sellerProducts.products.map((product: any) => ({
        value: product?.entityid?.toString() || "N/A",
        label: product?.name || "Unnamed Product",
        image: product?.image1?.url || "https://via.placeholder.com/50",
        stockQty: product?.stock_data?.qty || 0,
        seller_reference: product?.seller_reference || "No REF",
      }));
      
      setProductOptions(options);
    }
  }, [productsData]);
  
  // Format date for API
  const formatDateForApi = (date: Date): string => {
    const pad = (num: number) => num.toString().padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  };
  
  // Get product details (name, stock, and image) from GraphQL data or API call
  const getProductDetails = async (productId: string): Promise<{ name: string, stock: number, imageUrl: string }> => {
    if (!productId) return { name: "Unknown Product", stock: 0, imageUrl: "https://via.placeholder.com/50" };
    
    // First check if product is in the existing options
    if (productsData?.sellerProducts?.products) {
      const product = productsData.sellerProducts.products.find(
        (p: any) => p.entityid.toString() === productId
      );
      
      if (product) {
        return { 
          name: product.name,
          stock: product.stock_data?.qty || 0,
          imageUrl: product.image1?.url || "https://via.placeholder.com/50"
        };
      }
    }
    
    // Otherwise fetch it using the IDs filter for better performance
    try {
      const { data } = await fetchProducts({
        variables: {
          page: 1,
          pageSize: 1,
          filter: { 
            ids: [productId]
          },
        },
      });
      
      if (data?.sellerProducts?.products && data.sellerProducts.products.length > 0) {
        const product = data.sellerProducts.products[0];
        return {
          name: product.name || "Unknown Product",
          stock: product.stock_data?.qty || 0,
          imageUrl: product.image1?.url || "https://via.placeholder.com/50"
        };
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    }
    
    return { name: "Unknown Product", stock: 0, imageUrl: "https://via.placeholder.com/50" };
  };
  
  // Get just the product name (for backward compatibility)
  const getProductName = async (productId: string): Promise<string> => {
    const details = await getProductDetails(productId);
    return details.name;
  };
  
  // Handle quantity change
  const handleQuantityChange = (index: number, quantity: number) => {
    const updatedItems = [...editItems];
    
    // For "out" move type, ensure quantity doesn't exceed current stock
    if (request?.["Move Type"]?.toLowerCase() === "out" && 
        quantity > (updatedItems[index].currentStock || 0)) {
      toast.warning(`Cannot exceed current stock of ${updatedItems[index].currentStock}`);
      quantity = updatedItems[index].currentStock || 0;
    }
    
    // Create a new object to ensure state change detection works properly
    const updatedItem = {
      ...updatedItems[index],
      quantity: quantity,
      Qty: quantity.toString()
    };
    
    // Replace the item at the index with the updated item
    updatedItems[index] = updatedItem;
    
    // Update state with the new array to ensure proper re-rendering
    setEditItems([...updatedItems]);
  };
  
  // Add a new product row
  const handleAddProduct = () => {
    setEditItems([
      ...editItems,
      {
        ID: "", // Will be filled when a product is selected
        Qty: "0",
        product_id: "",
        quantity: 0,
        productName: "Select a product",
        isNew: true
      }
    ]);
  };
  
  // Delete a product row
  const handleDeleteProduct = (index: number) => {
    // Prevent deletion if there's only one product
    if (editItems.length <= 1) {
      toast.warning("At least one product must be included");
      return;
    }
    
    const updatedItems = [...editItems];
    updatedItems.splice(index, 1);
    setEditItems(updatedItems);
  };
  
  // Handle product selection
  const handleProductChange = async (selected: any, index: number) => {
    if (!selected) return;
    
    const productId = selected.value;
    
    // Check if this product is already selected in another row
    const isDuplicate = editItems.some((item, idx) => 
      idx !== index && item.product_id === productId && !item.isNew
    );
    
    if (isDuplicate) {
      toast.error("This product is already selected in another row.");
      return;
    }
    
    const updatedItems = [...editItems];
    
    // Create a new object with updated properties
    const updatedItem = {
      ...updatedItems[index],
      ID: productId,
      product_id: productId,
      productName: selected.label,
      currentStock: selected.stockQty || 0,
      imageUrl: selected.image,
      seller_reference: selected.seller_reference,
      isNew: false // No longer a new item once a product is selected
    };
    
    // Replace the item at the index
    updatedItems[index] = updatedItem;
    
    // Use a new array reference to ensure state change is detected
    setEditItems([...updatedItems]);
  };
  
  // Load products with search and pagination
  const loadProducts = async (inputValue: string, page = 1) => {
    try {
      setSearchTerm(inputValue);
      
      if (!request) {
        return;
      }
      
      // Determine the stock type and move type from the request
      const moveType = request["Move Type"]?.toLowerCase() || '';
      let stockType: string;
      
      // Handle stock type which can come as a string or a boolean
      if (typeof request["Stock Type"] === 'string') {
        stockType = request["Stock Type"] as string;
      } else {
        stockType = request["Stock Type"] ? 'FBW' : 'drop';
      }
      
      console.log(`Loading products for ${moveType} movement, ${stockType} stock type`);
      
      // Create filter object with all needed properties
      const filter: any = {
        type_id: "simple", // Always use simple products
        // Set mode_expedition filter based on stock type
        ...(stockType === 'FBW' 
          ? { mode_expedition: 16500 } // FBW products
          : { mode_expedition_neq: 16500 }), // drop (Easy Shipping) products
        
        // For "out" movement, add filter for products with stock > 0
        ...(moveType === 'out' ? { qty_gt: 0 } : {}),
        
        // Add search term filter if provided
        ...(inputValue && inputValue.length > 0 ? { name: inputValue } : {})
      };
      
      console.log("Using filter:", filter);
      
      const { data } = await fetchProducts({
        variables: {
          page: page,
          pageSize: 20,
          filter: filter,
        },
      });
      
      if (data?.sellerProducts?.products) {
        const newOptions = data.sellerProducts.products.map((product: any) => ({
          value: product?.entityid?.toString() || "N/A",
          label: product?.name || "Unnamed Product",
          image: product?.image1?.url || "https://via.placeholder.com/50",
          stockQty: product?.stock_data?.qty || 0,
          seller_reference: product?.seller_reference || "No REF",
        }));
        
        // If page is 1, replace options; otherwise append
        if (page === 1) {
          setProductOptions(newOptions);
        } else {
          // Append new options, avoiding duplicates
          setProductOptions(prevOptions => {
            const existingIds = new Set(prevOptions.map((p: ProductOption) => p.value));
            const uniqueNewOptions = newOptions.filter((p: ProductOption) => !existingIds.has(p.value));
            return [...prevOptions, ...uniqueNewOptions];
          });
        }
        
        // Update current page
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };
  
  // Handle load more when scrolling to bottom
  const handleMenuScrollToBottom = () => {
    if (isMenuOpen && !productsLoading) {
      const nextPage = currentPage + 1;
      console.log(`Loading more products, page ${nextPage}`);
      loadProducts(searchTerm, nextPage);
    }
  };
  
  // Check if there are changes in the form to enable/disable update button
  const checkForChanges = (): boolean => {
    if (!originalDate || !editDate) return false;

    // 1. Compare date
    if (originalDate.getTime() !== editDate.getTime()) {
      return true;
    }

    // 2. Compare number of items
    if (originalItems.length !== editItems.length) {
      return true;
    }

    // 3. Compare each item by ID, product_id, and quantity
    for (const item of editItems) {
      const original = originalItems.find(o => o.ID === item.ID);
      if (!original || original.product_id !== item.product_id || original.quantity !== item.quantity) {
        return true;
      }
    }

    // 4. Detect removed items
    for (const originalItem of originalItems) {
      const stillExists = editItems.find(i => i.ID === originalItem.ID);
      if (!stillExists) {
        return true;
      }
    }

    return false;
  };
  
  // UseEffect to check for changes whenever editDate or editItems are updated
  useEffect(() => {
    const hasChanges = checkForChanges();
    setHasFormChanges(hasChanges);
    console.log("Form changes detected:", hasChanges);
  }, [editDate, editItems, originalDate, originalItems]);
  
  // Submit updated inventory request
  const handleUpdateSubmit = async () => {
    if (!request || !editDate) {
      toast.error("Missing required information");
      return;
    }
    
    // Validate
    const invalidItems = editItems.filter(item => !item.ID || item.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error("All products must have a valid ID and quantity");
      return;
    }
    
    // For "out" moves, validate quantities don't exceed current stock
    if (request?.["Move Type"]?.toLowerCase() === "out") {
      const overStockItems = editItems.filter(item => 
        item.quantity > (item.currentStock || 0)
      );
      
      if (overStockItems.length > 0) {
        toast.error("Cannot remove more items than current stock for one or more products");
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      const formattedDate = formatDateForApi(editDate);
      // Use the ID and Qty properties directly since they're already in the correct format
      const items = editItems.map(item => ({
        ID: item.ID,
        Qty: item.quantity.toString(), // Update the quantity with the latest value
      }));
      
      // Use GraphQL mutation
      const { data: updateData } = await updateInventoryRequestMutation({
        variables: {
          request_id: request.inv_id,
          date: formattedDate,
          items: items
        }
      });
      
      if (updateData?.updateInventoryRequest?.status === "success") {
        toast.success(updateData.updateInventoryRequest.message || "Inventory request updated successfully");
        onHide();
        onRequestUpdated(); // Refresh the list
      } else {
        toast.error(updateData?.updateInventoryRequest?.message || "Failed to update inventory request");
      }
    } catch (error) {
      console.error("Error updating inventory request:", error);
      toast.error("Failed to update inventory request");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      size="lg"
      centered
      backdrop="static"
      className="rounded-3 overflow-hidden"
    >
      <Modal.Header 
        className={`${
          request?.["Move Type"]?.toLowerCase() === "in" ? "bg-success bg-opacity-10 border-bottom border-success" : 
          request?.["Move Type"]?.toLowerCase() === "out" ? "bg-danger bg-opacity-10 border-bottom border-danger" : 
          "bg-light border-bottom border-light"
        }`}
      >
        <Modal.Title className={`${
          request?.["Move Type"]?.toLowerCase() === "in" ? "text-success" : 
          request?.["Move Type"]?.toLowerCase() === "out" ? "text-danger" : 
          "text-secondary"
        }`}>
          <FontAwesomeIcon icon={faEdit} className="me-2" />
          {t("inventoryDetails.edit")} {request?.inv_id || ""}
          {request?.["Move Type"]?.toLowerCase() === "in" && (
            <span className="badge border border-success text-success bg-body ms-2">{t("inventoryHistory.moveType.in")}</span>
          )}
          {request?.["Move Type"]?.toLowerCase() === "out" && (
            <span className="badge border border-danger text-danger bg-body ms-2">{t("inventoryHistory.moveType.out")}</span>
          )}
        </Modal.Title>
        <button 
          type="button" 
          className="btn-close rounded-2 px-4" 
          onClick={onHide}
          aria-label="Close"
        ></button>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form>
          <Row className="mb-4">
            {/* <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="py-3 align-middle">Request ID</Form.Label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FontAwesomeIcon icon={faEdit} className="text-secondary" />
                  </span>
                  <Form.Control 
                    type="text" 
                    value={request?.inv_id || ""} 
                    disabled 
                    className="border-start-0 bg-white ps-1 "
                  />
                </div>
              </Form.Group>
            </Col> */}
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label className="py-3 align-middle">{t("inventoryDetails.scheduledDate")}</Form.Label>
                <div className="input-group">
                  <span className="input-group-text bg-light border-end-0">
                    <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary" />
                  </span> 
                  <Flatpickr
                    className="form-control border-start-0 ps-1"
                    value={editDate || undefined}
                    onChange={([date]) => setEditDate(date)}
                    options={{ 
                      enableTime: true, 
                      time_24hr: true,
                      dateFormat: "d/m/Y H:i",
                      allowInput: true
                    }}
                  />
                </div>
              </Form.Group>
            </Col>
          </Row>
          
          <div className="card border-0 shadow-sm mb-4 rounded-3 overflow-hidden">
            
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                    <thead className="bg-body-tertiary">
                      <tr>
                        <th className="border-0 py-3">{t("inventory.product")}</th>
                        <th className="border-0 py-3 text-center" style={{ width: "25%" }}>{t("inventory.currentStock")}</th>
                        <th className="border-0 py-3 text-center" style={{ width: "25%" }}>{t("inventory.newStock", "New Stock")}</th>
                        <th className="border-0 py-3 text-center" style={{ width: "15%" }}>{t("inventory.actions")}</th>
                      </tr>
                    </thead>
                  <tbody>
                    {editItems.map((item, index) => (
                      <tr key={index}>
                        <td className="py-3 align-middle">
                          {item.isNew ? (
                            <Select
                              options={productOptions.filter(option => 
                                // Filter out products that are already selected in other rows
                                !editItems.some(
                                  item => item.product_id === option.value && !item.isNew
                                )
                              )}
                              placeholder={t("inventory.searchProduct")}
                              onChange={(selected) => handleProductChange(selected, index)}
                              isLoading={productsLoading}
                              onInputChange={(value) => {
                                if (value !== searchTerm) {
                                  loadProducts(value, 1);
                                }
                              }}
                              onMenuOpen={() => {
                                setIsMenuOpen(true);
                                if (productOptions.length === 0) {
                                  loadProducts("", 1);
                                }
                              }}
                              onMenuClose={() => setIsMenuOpen(false)}
                              onMenuScrollToBottom={handleMenuScrollToBottom}
                              formatOptionLabel={(option: ProductOption) => (
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <div 
                                    style={{ 
                                      width: 40, 
                                      height: 40, 
                                      marginRight: 10, 
                                      borderRadius: "4px", 
                                      overflow: "hidden",
                                      border: "1px solid #dee2e6"
                                    }}
                                  >
                                    <img
                                      src={option.image}
                                      alt={option.label}
                                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                    />
                                  </div>
                                  <div>
                                    <div style={{ fontWeight: 500 }}>{option.label}</div>
                                    <div style={{ fontSize: "0.8rem", color: "var(--bs-body-color-rgb)" }}>{t("inventory.ref")}: {option.seller_reference}</div>
                                  </div>
                                </div>
                              )}
                              filterOption={() => true} // Skip built-in filtering, we'll handle it with API calls
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: (base) => ({
                                  ...base,
                                  zIndex: 9999
                                })
                              }}
                            />
                          ) : (
                            <div className="d-flex align-items-center">
                              <div 
                                style={{ 
                                  width: 40, 
                                  height: 40, 
                                  marginRight: 10, 
                                  borderRadius: "4px", 
                                  overflow: "hidden",
                                  border: "1px solid #dee2e6"
                                }}
                              >
                                <img
                                  src={item.imageUrl || "https://via.placeholder.com/50"}
                                  alt={item.productName}
                                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                />
                              </div>
                              <div>
                                <div className="fw-semibold">{item.productName || "Unknown Product"}</div>
                                <div className="small text-muted">{t("inventory.ref")}: {item.seller_reference || t("inventory.noReference")}</div>
                              </div>
                            </div>
                          )}
                          </td>
                        <td className="py-3 align-middle text-center">
                          <div className="d-inline-block px-3 py-2 rounded-1" style={{ backgroundColor: "var(--bs-tertiary-bg)", fontWeight: 500 }}>
                            {item.currentStock || 0}
                          </div>
                        </td>
                        <td className="py-3 align-middle">
                          <div className="d-flex justify-content-center">
                            <div className="input-group" style={{ maxWidth: "100px" }}>
                              <Form.Control 
                                type="number" 
                                min="0"
                                max={request?.["Move Type"]?.toLowerCase() === "out" ? item.currentStock || 0 : undefined}
                                value={item.quantity} 
                                onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                                className={`text-center ${
                                  request?.["Move Type"]?.toLowerCase() === "out" && 
                                  item.quantity >= (item.currentStock || 0) ? "border-danger" : ""
                                }`}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-3 align-middle text-center">
                          <RBButton
                            variant={editItems.length <= 1 ? "outline-secondary" : "outline-danger"}
                            size="sm"
                            onClick={() => handleDeleteProduct(index)}
                            title={editItems.length <= 1 ? "Cannot delete the last product" : "Delete this product"}
                            disabled={editItems.length <= 1}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </RBButton>
                        </td>
                      </tr>
                    ))}
                    {editItems.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-4 text-muted">
                          {t("inventory.noProductsFound")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-header bg-body-tertiary py-3 border-bottom d-flex justify-content-between align-items-center">
              <RBButton
                variant="outline-primary"
                size="sm"
                onClick={handleAddProduct}
                className="rounded-2"
              >
                <FontAwesomeIcon icon={faPlus} className="me-1" /> {t("inventory.addProductButton")}
              </RBButton>
            </div>
          </div>
        </Form>
      </Modal.Body>
      <Modal.Footer className="bg-body-tertiary border-top p-3">
        <RBButton 
          variant="outline-secondary" 
          onClick={onHide}
          className="rounded-2 px-4"
        >
          {t("inventoryDetails.cancel")}
        </RBButton>
        <RBButton 
          variant="primary" 
          onClick={handleUpdateSubmit}
          className="rounded-2 px-4 ms-2"
          disabled={isSubmitting || !hasFormChanges}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {t("inventoryDetails.updating", "Updating...")}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faEdit} className="me-2" />
              {t("inventoryDetails.updateRequest", "Update Request")}
            </>
          )}
        </RBButton>
      </Modal.Footer>
    </Modal>
  );
};

export default EditInventoryRequestModal;
